import {
  Button,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useDateRange } from "contexts/DateRangeContext";
import dayjs, { Dayjs, ManipulateType } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { ReactNode, useCallback, useMemo } from "react";
import { SafeDatePicker } from "components/SafeDatePicker";

type Duration = `${number} ${ManipulateType}`;
const parseDuration = (duration: Duration): [number, ManipulateType] => {
  const [amount, unit] = duration.split(" ") as [string, ManipulateType];
  return [parseInt(amount), unit];
};

type DurationPreset = Readonly<{ id: string; duration: Duration }>;
type RangePreset = Readonly<{
  id: string;
  getRange: (now: Dayjs) => { start: Dayjs; end: Dayjs };
}>;
export type ZoomPreset = DurationPreset | RangePreset;

const DEFAULT_ZOOM_PRESETS: ZoomPreset[] = [
  { id: "1week", duration: "1 week" },
  { id: "1month", duration: "1 month" },
  {
    id: "currentMonth",
    getRange: (now) => ({ start: now.startOf("month"), end: now }),
  },
  { id: "3months", duration: "3 months" },
  { id: "6months", duration: "6 months" },
  { id: "1year", duration: "1 year" },
];

const DAYS_THRESHOLD_TO_MATCH_ZOOM_LEVEL = 1;
const detectCurrentZoom = (
  start: Dayjs,
  end: Dayjs,
  direction: "backward" | "forward",
  presets: ZoomPreset[],
  now: Dayjs
): string | null => {
  for (const preset of presets) {
    if ("getRange" in preset) {
      const range = preset.getRange(now);
      if (
        Math.abs(start.diff(range.start, "day")) <=
          DAYS_THRESHOLD_TO_MATCH_ZOOM_LEVEL &&
        Math.abs(end.diff(range.end, "day")) <=
          DAYS_THRESHOLD_TO_MATCH_ZOOM_LEVEL
      ) {
        return preset.id;
      }
    } else {
      const [value, unit] = parseDuration(preset.duration);
      const expected =
        direction === "backward"
          ? end.subtract(value, unit)
          : start.add(value, unit);
      const actual = direction === "backward" ? start : end;
      const diffDays = Math.abs(actual.diff(expected, "day"));
      if (diffDays <= DAYS_THRESHOLD_TO_MATCH_ZOOM_LEVEL) {
        return preset.id;
      }
    }
  }
  return null;
};

interface DateRangeSelectorProps {
  direction?: "backward" | "forward";
  zoomPresets?: ZoomPreset[];
  rangeType?: "duration" | "inclusiveDates";
  timezone?: string;
  actions?: ReactNode;
}

export const DateRangeSelector = ({
  direction = "backward",
  zoomPresets = DEFAULT_ZOOM_PRESETS,
  rangeType = "duration",
  timezone,
  actions,
}: DateRangeSelectorProps) => {
  const { t } = useTenantTranslation();
  const { startDate, setStartDate, endDate, setEndDate } = useDateRange();

  const getNow = useCallback(
    () => (timezone ? dayjs().tz(timezone).startOf("day") : dayjs()),
    [timezone]
  );
  const currentZoom = detectCurrentZoom(
    startDate,
    endDate,
    direction,
    zoomPresets,
    getNow()
  );

  const currentDurationMs = useMemo(
    () => endDate.valueOf() - startDate.valueOf(),
    [startDate, endDate]
  );

  const currentDurationDays = endDate.diff(startDate, "day");
  const usesInclusiveDates = rangeType === "inclusiveDates";

  const handleZoomChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newZoom: string | null) => {
      if (!newZoom) return;

      const preset = zoomPresets.find((p) => p.id === newZoom);
      if (!preset) return;

      const now = getNow();
      if ("getRange" in preset) {
        const range = preset.getRange(now);
        setStartDate(range.start);
        setEndDate(range.end);
      } else {
        const [value, unit] = parseDuration(preset.duration);
        if (direction === "forward") {
          setStartDate(now);
          setEndDate(now.add(value, unit));
        } else {
          setStartDate(now.subtract(value, unit));
          setEndDate(now);
        }
      }
    },
    [setStartDate, setEndDate, direction, zoomPresets, getNow]
  );

  const handleShiftWindow = useCallback(
    (shiftDirection: -1 | 1) => {
      const deltaMs = currentDurationMs * shiftDirection;
      const deltaDays = (currentDurationDays + 1) * shiftDirection;
      const newStart = usesInclusiveDates
        ? startDate.add(deltaDays, "day")
        : dayjs(startDate.valueOf() + deltaMs);
      const newEnd = usesInclusiveDates
        ? endDate.add(deltaDays, "day")
        : dayjs(endDate.valueOf() + deltaMs);
      setStartDate(newStart);
      setEndDate(newEnd);
    },
    [
      startDate,
      endDate,
      currentDurationMs,
      currentDurationDays,
      setStartDate,
      setEndDate,
      usesInclusiveDates,
    ]
  );

  const handleJumpToNow = useCallback(() => {
    const now = getNow();
    if (direction === "forward") {
      setStartDate(now);
      setEndDate(
        usesInclusiveDates
          ? now.add(currentDurationDays, "day")
          : dayjs(now.valueOf() + currentDurationMs)
      );
    } else {
      setStartDate(
        usesInclusiveDates
          ? now.subtract(currentDurationDays, "day")
          : dayjs(now.valueOf() - currentDurationMs)
      );
      setEndDate(now);
    }
  }, [
    currentDurationMs,
    setStartDate,
    setEndDate,
    direction,
    getNow,
    usesInclusiveDates,
    currentDurationDays,
  ]);

  const handleStartDateChange = useCallback(
    (newValue: Dayjs | null) => {
      if (newValue?.isValid()) {
        setStartDate(newValue);
      }
    },
    [setStartDate]
  );

  const handleEndDateChange = useCallback(
    (newValue: Dayjs | null) => {
      if (newValue?.isValid()) {
        setEndDate(newValue);
      }
    },
    [setEndDate]
  );

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <SafeDatePicker
          label={t("startNoun")}
          value={startDate}
          onChange={handleStartDateChange}
          slotProps={{ textField: { size: "small" } }}
          format="YYYY-MM-DD"
        />
        <Typography variant="body2" color="text.secondary">
          –
        </Typography>
        <SafeDatePicker
          label={t("endNoun")}
          value={endDate}
          onChange={handleEndDateChange}
          slotProps={{ textField: { size: "small" } }}
          format="YYYY-MM-DD"
        />
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <ToggleButtonGroup
          size="small"
          value={currentZoom}
          exclusive
          onChange={handleZoomChange}
          sx={{
            "& .MuiToggleButton-root": {
              px: 1.5,
              py: 0,
              minHeight: 24,
              fontSize: "0.75rem",
              textTransform: "none",
            },
          }}
        >
          {zoomPresets.map((preset) => (
            <ToggleButton key={preset.id} value={preset.id}>
              {t(preset.id)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <IconButton
          aria-label={t("previous")}
          onClick={() => {
            handleShiftWindow(-1);
          }}
          size="small"
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          aria-label={t("next")}
          onClick={() => {
            handleShiftWindow(1);
          }}
          size="small"
        >
          <ChevronRightIcon />
        </IconButton>

        <Button
          size="small"
          onClick={handleJumpToNow}
          sx={{ textTransform: "none" }}
        >
          {t("jumpToNow")}
        </Button>
        {actions}
      </Stack>
    </Stack>
  );
};
